/**
 * Contact API
 */

import { apiClient } from '@shared/api/client';
import type {
    Contact,
    ContactListParams,
    ContactListResponse,
    CreateIndividualData,
    CreateIndividualPayload,
    CreateCorporateData,
    UpdateContactData,
    CompanyRelation,
    ContactDeleteBlocker,
    ContactDeleteBlockersResponse,
    KYCDocumentStatus,
    KYCHistory,
    FileUploadResponse
} from '../types/contact.types';

export const contactsApi = {
    /**
     * Contact 목록 조회
     */
    getContacts: (params?: ContactListParams) => {
        return apiClient.get<ContactListResponse>('/Contact', { params });
    },

    /**
     * Contact 상세 조회
     * @param entityCd Entity_my 등 엔티티 컨텍스트 — L_CONTACT_BRANCH_MAP.ENTITY_CD 필터
     * @param positionCd Register 역할 코드 (B30027 Director 등) — L_CONTACT_BRANCH_MAP.POSITION_CD 필터
     */
    getContactById: (id: string, params?: { entityCd?: string; positionCd?: string }) => {
        return apiClient.get<{ contact: Contact }>(`/Contact/${id}`, { params });
    },

    /**
     * Dev-only: SPC 마이그레이션 주소(MIG_L_GLOBAL_CONTACT_ADDRESS_SPC) 조회
     */
    getDevSpcContactAddresses: (contactCd: string) => {
        return apiClient.get<{ success: boolean; contactCd: string; rows: any[] }>(
            `/dev/spc-contact-addresses/${encodeURIComponent(contactCd)}`
        );
    },

    /** LETTAGA address candidates only — L_CONTACT_ADDRESS / L_CONTACT_BRANCH (Select Address). */
    getContactAddressCandidates: (contactCd: string) => {
        return apiClient.get<import('../types/contact.types').ContactAddressCandidatesResponse>(
            `/Contact/${encodeURIComponent(contactCd)}/address-candidates`
        );
    },

    /** MY 주소 Consolidate — 병합 대상 주소 목록(역할수 배지). IS_DEL<>1 만. */
    getAddressConsolidateList: (contactCd: string) => {
        return apiClient.get<{
            contactCd: string;
            storage: 'branch' | 'address';
            addresses: Array<{
                id: number;
                addressType: string;
                addressTypeLabel: string;
                alias: string;
                displayLine: string;
                roleCount: number;
            }>;
        }>(`/Contact/${encodeURIComponent(contactCd)}/address-consolidate/list`);
    },

    /** MY 주소 Consolidate — keep/merge 재지정 계획(Review). */
    getAddressConsolidatePreview: (contactCd: string, keepId: number, mergeId: number) => {
        return apiClient.get<{
            contactCd: string;
            keepId: number;
            mergeId: number;
            keepMaps: Array<{ entityCd: string; companyName: string; positionCd: string; position: string; addrType: string; addrTypeLabel: string }>;
            reassignments: Array<{ entityCd: string; companyName: string; positionCd: string; position: string; addrType: string; addrTypeLabel: string; action: 'reassign' | 'drop' }>;
            collisionCount: number;
            reassignCount: number;
        }>(
            `/Contact/${encodeURIComponent(contactCd)}/address-consolidate/preview`
            + `?keepId=${encodeURIComponent(String(keepId))}&mergeId=${encodeURIComponent(String(mergeId))}`
        );
    },

    /** MY 주소 Consolidate — 커밋(MAP 재지정 + 옵션 soft delete). */
    commitAddressConsolidate: (
        contactCd: string,
        payload: { keepId: number; mergeId: number; deleteOrphan?: boolean }
    ) => {
        return apiClient.post<{
            contactCd: string;
            storage: 'branch' | 'address';
            keepId: number;
            mergeId: number;
            deleted: boolean;
            success: boolean;
        }>(`/Contact/${encodeURIComponent(contactCd)}/address-consolidate`, payload);
    },

    /**
     * Individual Contact 생성
     */
    createIndividual: (data: CreateIndividualPayload | CreateIndividualData) => {
        return apiClient.post<{ contactCd: string }>('/Contact/individual', data);
    },

    /**
     * Corporate Contact 생성
     */
    createCorporate: (data: CreateCorporateData) => {
        return apiClient.post<{ contactCd: string }>('/Contact/corporate', data);
    },

    /**
     * Contact 수정
     */
    updateContact: (id: string, data: UpdateContactData) => {
        return apiClient.put(`/Contact/${id}`, data);
    },

    /**
     * Contact 삭제
     */
    deleteContact: (id: string) => {
        return apiClient.delete(`/Contact/${id}`);
    },

    /**
     * Contact 삭제 차단 여부 및 연관 Entity 조회
     */
    getDeleteBlockers: (contactId: string) => {
        return apiClient.get<ContactDeleteBlockersResponse>(
            `/Contact/${contactId}/delete-blockers`
        );
    },

    /**
     * Contact 병합 미리보기 — 각 sub 컨택트가 참조 중인 엔티티 목록.
     */
    getMergePreview: (payload: { mainContactCd: string; subContactCds: string[] }) => {
        return apiClient.post<{
            main: { contactCd: string; name: string; contactRegNo?: string; categoryCd?: string };
            subs: Array<{
                contactCd: string;
                name: string;
                contactRegNo?: string;
                categoryCd?: string;
                references: ContactDeleteBlocker[];
            }>;
        }>('/Contact/merge/preview', payload);
    },

    /**
     * Contact 병합 실행 — sub 컨택트들의 엔티티 참조를 main 으로 재지정하고 sub 은 soft delete.
     */
    mergeContacts: (payload: { mainContactCd: string; subContactCds: string[] }) => {
        return apiClient.post<{
            mainContactCd: string;
            mergedSubCount: number;
            perSubResult: Array<{ subContactCd: string; reassignCounts: Record<string, number> }>;
        }>('/Contact/merge', payload);
    },

    /**
     * Individual 전용: Company Relations 조회 (페이지네이션·회사명 검색)
     */
    getCompanyRelations: (
        contactId: string,
        params?: { limit?: number; offset?: number; search?: string }
    ) => {
        return apiClient.get<{
            companyRelations: CompanyRelation[];
            totalCount?: number;
            hasMore?: boolean;
            offset?: number;
            limit?: number;
        }>(`/Contact/${contactId}/company-relations`, { params });
    },

    /**
     * Company Relations 엑셀 다운로드 전 권한 검증.
     * 현재 로그인 client가 해당 contact의 소유(client)가 아니면 백엔드가 403(AUTH002)을 반환한다.
     */
    verifyCompanyRelationsAccess: (contactId: string) => {
        return apiClient.get<{ allowed: boolean }>(
            `/Contact/${contactId}/company-relations/access`
        );
    },

    /**
     * Individual 전용: KYC Documents 조회
     */
    getKYCDocuments: (contactId: string) => {
        return apiClient.get<{ documents: KYCDocumentStatus }>(`/Contact/${contactId}/kyc/documents`);
    },

    /**
     * Individual 전용: KYC History 조회
     */
    getKYCHistory: (contactId: string) => {
        return apiClient.get<{ history: KYCHistory[] }>(`/Contact/${contactId}/kyc/history`);
    },

    /**
     * Individual 전용: KYC Renewal 요청
     */
    requestKYCRenewal: (contactId: string) => {
        return apiClient.post(`/Contact/${contactId}/kyc/renewal`);
    },

    /**
     * Postal Code 검색 (Local Address 자동 완성)
     */
    searchPostalCode: (postalCode: string) => {
        return apiClient.get<{
            blockHouseNo?: string;
            streetName: string;
            buildingEstateName?: string;
        }>('/Contact/search-postal-code', {
            params: { postalCode },
        });
    },

    /**
     * Contact 엑셀 업로드 (배치 생성)
     */
    uploadContacts: (data: {
        contactType: 'Individual' | 'Corporate';
        data: Array<CreateIndividualData | CreateCorporateData>;
    }) => {
        return apiClient.post<{
            total: number;
            success: number;
            failed: number;
            results: Array<{
                index: number;
                success: boolean;
                contactCd?: string;
                error?: string;
            }>;
        }>('/Contact/upload', data);
    },
};

